"use client";

import { SegmentControl } from "@/components/SegmentControl";
import BlokWrap from "@/components/bloks/BlokWrap";
import { Carousel } from "@/components/ui/Carousel";
import type { BlokRouter } from "@/data/routes/bloks.route";
import { ProductCardWithCampaigns } from "@/features/campaigns";
import { ProductCardSkeleton } from "@/features/products/ProductCard";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokProducts } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import type { StoryblokMultilink } from "storyblok";
import { sizes } from "../Asset";
import styles from "./Products.module.css";

export const ProductsBlok = ({
	blok,
	data = [],
	index = 0,
}: BlokProps<BlokProducts, BlokRouter["products"]>) => {
	const link = sbLink(blok.link as StoryblokMultilink);
	return (
		<SegmentControl segments={blok}>
			<BlokWrap data-animate editable={blok} className={styles.root}>
				<Carousel
					data-fade
					activeScroll={data?.length > 4}
					showControls={data?.length > 4}
					title={blok.title}
					link={link}
					linkText={blok.linkText}
					onLinkClick={() =>
						trackContentClick({
							contentType: blok.component,
							itemId: blok._uid,
							href: link?.href,
							label: blok.linkText,
						})
					}
				>
					{data?.map((product) => {
						return (
							<ProductCardWithCampaigns
								sizes={sizes(75, 50)}
								key={product.displayItemId}
								product={product}
								showRatings
							/>
						);
					})}
				</Carousel>
			</BlokWrap>
		</SegmentControl>
	);
};

export const ProductsBlokSkeleton = ({ blok }: BlokProps<BlokProducts>) => {
	const link = sbLink(blok.link as StoryblokMultilink);
	const data = blok.products?.slice(0, 5);

	const count = data?.length ?? 0;

	return (
		<BlokWrap editable={blok} className={styles.root}>
			<Carousel
				title={blok.title}
				activeScroll={count > 4}
				showControls={count > 4}
				link={link}
				linkText={blok.linkText}
			>
				{blok.products?.slice(0, 5).map((product) => {
					return <ProductCardSkeleton key={product} />;
				})}
			</Carousel>
		</BlokWrap>
	);
};
